"use client";
import { useMemo, useState } from "react";
import { useT } from "@/lib/i18n";
import { PageHeading } from "@/components/ui";
import { BookOpen, ExternalLink, FileText, Shield, Terminal, Copy, Check, Search, TerminalSquare, MonitorCog } from "lucide-react";
import { CODE_LIBRARY, CODE_TOTAL } from "@/lib/library/code-library";

/**
 * Кітапхана — таңдалған нақты оқу материалдары (OWASP, PortSwigger,
 * NIST, Kali docs, HTB) + платформа ішіндегі cheat sheet-тер.
 */

interface LibItem {
  title: string;
  desc: string;
  url?: string;
  tag: string;
}

type LibGroup = { groupKey: string; icon: "book" | "shield" | "file"; items: LibItem[] };
const LIBRARY: LibGroup[] = [
  {
    groupKey: "lib.g_owasp",
    icon: "shield",
    items: [
      { title: "OWASP Top 10 (2021)", desc: "Веб-осалдықтардың негізгі 10 тізімі: SQLi, XXE, IDOR, SSRF… Арена сабақтары осы тізім бойынша.", url: "https://owasp.org/www-project-top-ten/", tag: "міндетті" },
      { title: "OWASP Web Security Testing Guide (WSTG)", desc: "Қалай тексеру керек: әр осалдық бойынша нақты тест қадамдары.", url: "https://owasp.org/www-project-web-security-testing-guide/", tag: "практика" },
      { title: "OWASP Cheat Sheet Series", desc: "Қысқаша нұсқаулықтар: SQL Injection Prevention, Access Control, Logging…", url: "https://cheatsheetseries.owasp.org/", tag: "cheat sheet" },
    ],
  },
  {
    groupKey: "lib.g_exercises",
    icon: "book",
    items: [
      { title: "PortSwigger Web Security Academy", desc: "SQLi, XSS, IDOR — тегін, браузерде, әр тақырыпта labs. Ең жақсы бастау.", url: "https://portswigger.net/web-security", tag: "тегін" },
      { title: "TryHackMe", desc: "Жолдар (paths) бойынша жүйелі оқу: Pre-Security, Junior Penetration Tester.", url: "https://tryhackme.com/", tag: "жолдар" },
      { title: "HackTheBox — Machines & Pro Labs", desc: "Кешенді жүйелерді бұзу (CTF + real machines). Аренадағы форматқа жақын.", url: "https://www.hackthebox.com/", tag: "CFt/real" },
      { title: "OverTheWire: Bandit", desc: "Linux + SSH негіздері — тегін, терминалда. Kali-де ойнайды.", url: "https://overthewire.org/wargames/bandit/", tag: "linux" },
    ],
  },
  {
    groupKey: "lib.g_docs",
    icon: "file",
    items: [
      { title: "Kali Linux Documentation (tools.kali.org)", desc: "Әр құралдың ресми қолдану нұсқаулығы: nmap, hydra, sqlmap, metasploit…", url: "https://www.kali.org/docs/", tag: "көмекші" },
      { title: "NIST SP 800-115 — Technical Guide to Information Security Testing", desc: "Зертхана/аудит әдістемесі: қалай бағалайды, есептейді.", url: "https://csrc.nist.gov/pubs/sp/800/115/r2/final", tag: "стандарт" },
      { title: "NIST SP 800-61 — Incident Response Guide (PICERL)", desc: "Оқиғаға жауап: Preparing, Identification, Containment, Eradication, Recovery, Lessons Learned.", url: "https://csrc.nist.gov/pubs/sp/800/61/r2/final", tag: "IR" },
      { title: "Nmap Reference Guide", desc: "nmap-тің толық құжаттамасы: барлық flag-тер, scan түрлері.", url: "https://nmap.org/book/", tag: "көмекші" },
    ],
  },
];

const CHEATSHEETS: { title: string; body: string }[] = [
  {
    title: "nmap — желі сканерлеу",
    body: `ip a                      # өз Kali IP-ңді көр
nmap -sn 172.16.X.0/24    # ping sweep: тірі хосттар
nmap -sV -sC <target>     # барлық порттар + сервистер + скрип्टтер
nmap -sS -p- <target>     # SYN scan, барлық 65535 порт
nmap -A <target>          # OS анықтау + скрип्टтер + traceroute
# Нәтижені сақта:  nmap -sV -oN scan.txt <target>`,
  },
  {
    title: "hydra — brute force (тек өз зертханаңда!)",
    body: `# SSH-ге (мысал: target-те ssh:22 бар):
hydra -l admin -P /usr/share/wordlists/rockyou.txt <target> ssh
# Web-формаға:
hydra -l admin -P /usr/share/wordlists/rockyou.txt <target> http-post-form "/login:username=^USER^&password=^PASS^:F=incorrect"
# Нәтиже: «PASSWORD : admin, Target2026!» тәрізді жол`,
  },
  {
    title: "curl — web зерттеу",
    body: `curl -s http://<target>:8080/api/orders/101        # өз order
curl -s http://<target>:8080/api/orders/1          # бәсекесіз IDOR тексеруі
curl -s http://<target>:8080/ -v                   # headers
curl -s -X POST http://<target>:8080/api/login -H "Content-Type: application/json" -d '{"user":"alice","pass":"a1ce"}'
# token-бен:
TOKEN=$(curl -s .../api/login -d ... | jq -r .token)
curl -s http://<target>:8080/api/orders -H "Authorization: Bearer $TOKEN"`,
  },
  {
    title: "sqlmap — SQL инъекциясы",
    body: `# Осал параметрді тапқан соң:
sqlmap -u "http://<target>:8080/api/items?id=1" --dbs
sqlmap -u "http://<target>:8080/api/items?id=1" -D shop -tables
sqlmap -u "http://<target>:8080/api/items?id=1" -D shop -T users --dump
# Өз зертханаңнан тыс URL-ге sqlmap жүргізугЕ БОЛМАЙДЫ.`,
  },
  {
    title: "Blue: логтарды талдау (grep/awk)",
    body: `tail -f /var/log/shop/access.log
# Барлық IP-лерді санау (кім көп сұраныс жолды?):
awk '{print $4}' /var/log/shop/access.log | sort | uniq -c | sort -rn | head
# Belгілі IP-нің істегені:
grep "ip=172.16.X.101" /var/log/shop/access.log
# 401/403 қателері (brute force/IDOR ізді):
awk '$6 ~ /401|403/' /var/log/shop/access.log | tail -20`,
  },
  {
    title: "Blue: қорғаныс (fix + iptables + fail2ban)",
    body: `# 1) IDOR түзетуі (fixer ретінде):
echo "access_check=on" | sudo tee /etc/shop/access_check.conf
# 2) Шыбуылшы IP-ні блоктау:
sudo iptables -A INPUT -s 172.16.X.101 -j DROP
sudo iptables -L INPUT -n --line-numbers
# 3) fail2ban: ssh-ге brute force болса автоматты ban
sudo fail2ban-client status sshd
# Тексеру: блокталған IP-ден curl → timeout болуы керек`,
  },
];

const Icon = ({ kind }: { kind: "book" | "shield" | "file" }) =>
  kind === "book" ? (
    <BookOpen size={18} />
  ) : kind === "shield" ? (
    <Shield size={18} />
  ) : (
    <FileText size={18} />
  );

// ---------- Код кітапханасы: 2000 команда, 3 OS (Kali/Parrot/Ubuntu) ----------
const PART_ICON: Record<string, React.ReactNode> = {
  kali: <Terminal size={15} />,
  parrot: <TerminalSquare size={15} />,
  ubuntu: <MonitorCog size={15} />,
};

function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else fallbackCopy(text);
}
function fallbackCopy(text: string) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  } catch {
    /* noop */
  }
}

function CodeLibrarySection() {
  const t = useT();
  const [partId, setPartId] = useState("kali");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [limit, setLimit] = useState(60);
  const [copied, setCopied] = useState<string | null>(null);
  const part = CODE_LIBRARY.find((p) => p.id === partId) ?? CODE_LIBRARY[0];
  const all = useMemo(
    () => part.categories.flatMap((c) => c.entries.map((e) => ({ cat: c.name, cmd: e.cmd, desc: e.desc }))),
    [part]
  );
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return all.filter(
      (e) => (cat === "all" || e.cat === cat) && (!s || e.cmd.toLowerCase().includes(s) || e.desc.toLowerCase().includes(s))
    );
  }, [all, q, cat]);
  const shown = filtered.slice(0, limit);
  const pickPart = (id: string) => {
    setPartId(id);
    setCat("all");
    setLimit(60);
  };
  return (
    <section className="card library-group code-lib">
      <h3 className="library-group-title">
        <Terminal size={18} /> {t("lib.code_title", { total: CODE_TOTAL })}
      </h3>
      <p className="muted small code-lib-sub">{t("lib.code_sub")}</p>
      <div className="code-tabs" role="tablist">
        {CODE_LIBRARY.map((p) => (
          <button
            key={p.id}
            role="tab"
            aria-selected={p.id === part.id}
            className={"code-tab" + (p.id === part.id ? " active" : "")}
            onClick={() => pickPart(p.id)}
          >
            {PART_ICON[p.id]} {p.os}
            <span className="code-tab-count">{p.categories.reduce((a, c) => a + c.entries.length, 0)}</span>
          </button>
        ))}
      </div>
      <div className="code-controls">
        <label className="code-search">
          <Search size={14} />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setLimit(60); }}
            placeholder={t("lib.code_search_ph")}
            aria-label={t("lib.code_search_ph")}
          />
        </label>
        <select className="code-cats" value={cat} onChange={(e) => { setCat(e.target.value); setLimit(60); }} aria-label="Категория">
          <option value="all">{t("lib.code_all")} ({part.categories.reduce((a, c) => a + c.entries.length, 0)})</option>
          {part.categories.map((c) => (
            <option key={c.name} value={c.name}>{c.name} ({c.entries.length})</option>
          ))}
        </select>
      </div>
      <div className="code-stat muted small">{filtered.length} {t("lib.code_found")} · {part.subtitle}</div>
      <div className="code-list">
        {shown.map((e) => (
          <div className="code-row" key={e.cmd}>
            <code className="code-cmd">{e.cmd}</code>
            <div className="code-row-right">
              <span className="code-desc muted small">{e.desc}</span>
              <button
                className={"code-copy" + (copied === e.cmd ? " done" : "")}
                onClick={() => { copyText(e.cmd); setCopied(e.cmd); setTimeout(() => setCopied(null), 1200); }}
                title={t("lib.code_copy")}
                aria-label={t("lib.code_copy") + ": " + e.cmd}
              >
                {copied === e.cmd ? <Check size={13} /> : <Copy size={13} />} {copied === e.cmd ? t("lib.code_copied") : t("lib.code_copy")}
              </button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="muted small">{t("lib.code_empty")}</div>}
      </div>
      {filtered.length > limit && (
        <button className="code-more" onClick={() => setLimit((l) => l + 120)}>
          {t("lib.code_show_more", { step: 120, rest: filtered.length - limit })}
        </button>
      )}
    </section>
  );
}

export function LibraryPage() {
  const t = useT();
  return (
    <div className="page">
      <PageHeading title={t("lib.title")} description={t("lib.subtitle")} />
      <div className="library-groups">
        {LIBRARY.map((g) => (
          <section className="card library-group" key={g.groupKey}>
            <h3 className="library-group-title">
              <Icon kind={g.icon} /> {t(g.groupKey)}
            </h3>
            <div className="library-items">
              {g.items.map((it) => (
                <a
                  className="library-item"
                  key={it.title}
                  href={it.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <div className="library-item-title">
                    {it.title}
                    <span className="badge-muted">{it.tag}</span>
                  </div>
                  <div className="muted small">{it.desc}</div>
                  <ExternalLink size={13} className="library-item-link" />
                </a>
              ))}
            </div>
          </section>
        ))}

        <CodeLibrarySection />

        <section className="card library-group">
          <h3 className="library-group-title">
            <FileText size={18} /> {t("lib.cheatsheets")}
          </h3>
          <div className="library-cheats">
            {CHEATSHEETS.map((c) => (
              <details className="cheat" key={c.title}>
                <summary>{c.title}</summary>
                <pre className="cheat-pre">{c.body}</pre>
              </details>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
